import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { getRdxActionMapper, getRdxSelectionMapper } from "rdx/utils/propsMapping";
import { useLocation, useHistory } from "react-router-dom";

import { cloneDeep } from "lodash";

import { parse, stringify } from "query-string";
import { EnterpriseCompModelEnum } from "rdx/modules/users/types/enums";
import messages from "rdx/modules/enterprisePersonnel/messages";

import MessageEvent from "models/MessageEvent";
import ModalContainer from "components/Modals/ModalContainer";
import styles from "./SeatRequestModal.module.less";

const SeatRequestModal = (props) => {
  const { rejectSeatInvite, acceptSeatInvite, getPendingSeat, pendingSeat, latestMessage } = props;

  const history = useHistory();
  const { search, pathname } = useLocation();
  const [accepting, setAccepting] = useState(false);

  const parsedSearch = parse(search);
  const pendingSeatId =
    typeof parsedSearch.seat_offer_modal === "string" && !Number.isNaN(Number(parsedSearch.seat_offer_modal))
      ? Number(parsedSearch.seat_offer_modal)
      : null;

  const pendingSponsorId =
    typeof parsedSearch.sponsor_id === "string" && !Number.isNaN(Number(parsedSearch.sponsor_id))
      ? Number(parsedSearch.sponsor_id)
      : null;

  const visible = pendingSeatId !== null;

  const { orgName, sellerPercentage, roleDisplay, compType } = pendingSeat;
  const isCogsCompType = compType === EnterpriseCompModelEnum.COGS;
  const isCoOwnerRole = roleDisplay?.toLowerCase().includes("co-owner");

  useEffect(() => {
    if (visible) getPendingSeat({ pendingSeatId });
  }, [pendingSeatId, visible, getPendingSeat]);

  useEffect(() => {
    if (!accepting) return;
    if (latestMessage?.message !== messages.ACCEPT_SEAT_INVITE_SUCCESS) return;

    setAccepting(false);
    history.push("/dashboard");
  }, [accepting, latestMessage, history]);

  const close = () => {
    const searchClone = cloneDeep(parsedSearch);
    delete searchClone.seat_offer_modal;
    delete searchClone.sponsor_id;

    history.push({
      pathname,
      search: stringify(searchClone),
    });
  };

  const handleDecline = () => {
    rejectSeatInvite({ pendingSeatId });
    close();
  };

  const handleSubmit = () => {
    setAccepting(true);
    acceptSeatInvite({ pendingSeatId, pendingSponsorId });
    close();
  };

  const beginsWithVowel = (string) => {
    if (!string) return false;

    return /[aeiouAEIOU]/.test(string[0]);
  };

  return (
    <ModalContainer
      visible={visible}
      title="Enterprise Seat Request"
      handleSubmit={handleSubmit}
      submitText="Accept"
      cancelText="Decline"
      handleCancel={handleDecline}
      centerButtons
      zIndex={1202}
      footerText="Would you like to accept this offer?"
      headerClassName={styles.seatRequestModalHeader}
      cancelButtonStyles={{ width: "156px" }}
      submitButtonStyles={{ width: "156px" }}
      onClose={close}
      xClosable
    >
      <div className={styles.seatRequestModal}>
        <div className={styles.header}>
          <span>
            <span>{orgName}</span> wants you on their team.
          </span>
        </div>
        <div className={styles.body}>
          <span>
            The Powur Enterprise organization, <span className={styles.bold}>{orgName}</span>, has requested that you
            become a{beginsWithVowel(roleDisplay) && "n"} {roleDisplay || "seated seller"} on their team.
          </span>
        </div>
        <div className={styles.body2}>
          {isCogsCompType && !isCoOwnerRole && (
            <span>
              {Number(sellerPercentage) > 0 ? (
                <>
                  In accepting this request your default compensation plan would be <span>{sellerPercentage}%</span> of
                  the project margin on self generated leads.
                </>
              ) : (
                <>
                  Your compensation plan will be customized by your team leader. Connect with{" "}
                  <span className={styles.bold}>{orgName}</span> to discuss your earning structure.
                </>
              )}
            </span>
          )}
        </div>
      </div>
    </ModalContainer>
  );
};

SeatRequestModal.propTypes = {
  rejectSeatInvite: PropTypes.func,
  acceptSeatInvite: PropTypes.func,
  getPendingSeat: PropTypes.func,
  latestMessage: MessageEvent.types(),
  pendingSeat: PropTypes.shape({
    orgName: PropTypes.string,
    sellerPercentage: PropTypes.number,
    roleDisplay: PropTypes.string,
    compType: PropTypes.oneOf([
      EnterpriseCompModelEnum.COGS,
      EnterpriseCompModelEnum.HOLDBACK,
      EnterpriseCompModelEnum.POWUR_LINE,
    ]),
  }),
};

export default connect(
  getRdxSelectionMapper({ pendingSeat: "selectPendingSeat", latestMessage: "getLatestMessageEvt" }),
  getRdxActionMapper(["rejectSeatInvite", "acceptSeatInvite", "getPendingSeat"]),
)(SeatRequestModal);
